import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { motion } from 'motion/react'
import React from 'react'
import styled, { css } from 'styled-components'
import { Drawer as VaulDrawer } from 'vaul'

import { Button } from '@blade/ui-kit'
import { CloseIcon } from '@blade/ui-kit'

import { DrawerHeader } from './drawer-header'

import type { TypeHelper } from '@blade/std-lib'
import type { Intent } from '@blade/ui-kit'
import type { ClassNameProps } from '@root/shared/react'
import type { ContentProps } from 'vaul'

const HEADER_TITLE_HEIGHT = 44
const HEADER_NO_TITLE_HEIGHT = 30

export type Props = {
  title: React.ReactNode
  snapPoints: boolean
  transparent?: boolean
  onClose?: () => void | undefined
  backgroundGradient?: Intent | 'primary' | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: React.Ref<any>
} & ClassNameProps &
  TypeHelper.OmitKey<ContentProps, 'title'>

export const DrawerContent = ({
  children,
  className,
  title,
  snapPoints,
  onClose,
  transparent = false,
  ref,
  backgroundGradient,

  ...contentProps
}: Props) => {
  return (
    <Content
      data-background-gradient={backgroundGradient}
      data-ignore-panel-swipe
      className={className}
      ref={ref}
      $snapPoints={snapPoints}
      $transparent={transparent}
      {...contentProps}
    >
      <DrawerHeader title={title} />
      <VisuallyHidden>
        <VaulDrawer.Title>{title ?? ''}</VaulDrawer.Title>
        <VaulDrawer.Description>{title ?? ''}</VaulDrawer.Description>
      </VisuallyHidden>
      <ContentInner $hasTitle={!!title}>{children}</ContentInner>
      {onClose ? (
        <CloseButton
          size="xs"
          variant="ghost"
          iconRight={<CloseIcon color="#fff" size={20} />}
          onClick={onClose}
        />
      ) : undefined}
    </Content>
  )
}

const ContentInner = styled(motion.div)<{ $hasTitle: boolean }>`
  position: relative;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  padding-top: ${({ $hasTitle }) =>
    $hasTitle ? HEADER_TITLE_HEIGHT : HEADER_NO_TITLE_HEIGHT}px;
  overflow: visible;

  display: grid;
  grid-template-rows: minmax(0, 1fr);
  grid-template-columns: minmax(0, 1fr);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
  > div {
    cursor: auto;
  }
`

const Content = styled(VaulDrawer.Content)<{
  $snapPoints: boolean
  $transparent: boolean
}>`
  outline: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  min-height: 0;
  max-height: var(--viewport-height);
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  overflow: visible;

  height: ${({ $snapPoints }) =>
    $snapPoints ? 'var(--viewport-height)' : 'auto'};

  ${(x) =>
    x.$transparent
      ? css`
          backdrop-filter: blur(25px);
        `
      : css`
          background-color: var(--background-1);
        `}

  // This is to style hidden area of the drawer, when user pulls it over
  &[data-vaul-drawer]:not([data-vaul-custom-container='true'])::after {
    /* background-image: none; */
  }

  &[data-background-gradient] {
    background-color: var(--appearance-1);
    background-repeat: repeat;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: -1;

      background:
        repeating-linear-gradient(
          0deg,
          rgba(var(--background-2-rgb), 0.5),
          rgba(var(--background-2-rgb), 0.5) 1px,
          transparent 1px,
          transparent 10px
        ),
        repeating-linear-gradient(
          90deg,
          rgba(var(--background-2-rgb), 0.5),
          rgba(var(--background-2-rgb), 0.5) 1px,
          transparent 1px,
          transparent 10px
        ),
        transparent;

      mask-image: radial-gradient(circle at 50% 0%, #0008 10%, transparent 80%);
    }
  }

  &[data-background-gradient='primary'] {
    &::after {
      z-index: -1;
      pointer-events: none;
      content: '';
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;

      background: radial-gradient(
        80% 60% at 50% -5%,
        var(--primaryA-8) 0%,
        var(--primaryA-3) 40%,
        transparent 100%
      );
    }
  }
  &[data-background-gradient='success'] {
    &::after {
      z-index: -1;
      content: '';
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;

      background: radial-gradient(
        80% 60% at 50% -5%,
        var(--successA-8) 0%,
        var(--successA-4) 40%,
        transparent 100%
      );
      pointer-events: none;
    }
  }
  &[data-background-gradient='error'] {
    &::after {
      z-index: -1;
      content: '';
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;

      background: radial-gradient(
        80% 60% at 50% -5%,
        var(--errorA-8) 0%,
        var(--errorA-4) 40%,
        transparent 100%
      );
      pointer-events: none;
    }
  }
  &[data-background-gradient='warning'] {
    &::after {
      z-index: -1;
      content: '';
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;

      background: radial-gradient(
        80% 60% at 50% -5%,
        var(--warningA-8) 0%,
        var(--warningA-5) 40%,
        transparent 100%
      );
      pointer-events: none;
    }
  }
  &[data-background-gradient='neutral'] {
    &::after {
      z-index: -1;
      content: '';
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;

      background: radial-gradient(
        80% 60% at 50% -5%,
        var(--neutralA-8) 0%,
        var(--neutralA-5) 40%,
        transparent 100%
      );
      pointer-events: none;
    }
  }
`

const CloseButton = styled(Button)`
  position: absolute;
  right: 9px;
  top: 8px;

  transition: background 0.2s ease-in-out;

  &:hover {
    background: var(--backgroundA-3);
  }
`
